import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle } from 'lucide-react';
import type { Intervention, InterventionRouteRequest } from '../../generated/api/types.gen.js';
import { zInterventionRouteRequest } from '../../generated/api/zod.gen.js';
import {
  getInterventionQueryKey,
  listInterventionsQueryKey,
  resolveInterventionMutation,
  routeInterventionMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Card, useToast } from '../../components/ui/index.js';
import {
  Form,
  JsonFormField,
  SelectFormField,
  SubmitButton,
  TextAreaFormField,
  useZodForm,
} from '../../components/form/index.js';
import { Can } from '../../components/auth/Can.js';
import { humanize } from './admin-format.js';
import { MANAGE_ACTION_REQUESTS, OUTCOMES } from './action-request-labels.js';
import { ActionRequestRejectModal } from './ActionRequestRejectModal.js';

const OUTCOME_OPTIONS = OUTCOMES.map((value) => ({ value, label: humanize(value) }));

interface ActionRequestRoutePanelProps {
  request: Intervention;
}

/** Route a request to an outcome (or close it as diagnostic only), or reject it. Recommended outcomes pre-select. */
export const ActionRequestRoutePanel: React.FC<ActionRequestRoutePanelProps> = ({ request }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [rejecting, setRejecting] = useState(false);
  const route = useMutation(routeInterventionMutation());
  const resolve = useMutation(resolveInterventionMutation());
  const open = request.status !== 'resolved' && request.status !== 'rejected';
  const path = { id: String(request.id) };

  const form = useZodForm(zInterventionRouteRequest, {
    defaultValues: { outcome: 'log_only', instructions: '', payload: {} },
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getInterventionQueryKey({ path }) }),
      queryClient.invalidateQueries({ queryKey: listInterventionsQueryKey() }),
    ]);

  const apply = async (values: InterventionRouteRequest) => {
    if (values.outcome === 'log_only') {
      await resolve.mutateAsync({
        path,
        body: { message: values.instructions || 'Resolved as diagnostic only.', payload: values.payload },
      });
    } else {
      await route.mutateAsync({ path, body: values });
    }
    await refresh();
    notify('Request updated');
  };

  const recommended = request.recommendedOutcomes;

  return (
    <>
      <Card title="Route this request">
        {open ? (
          <Can policies={MANAGE_ACTION_REQUESTS} mode="any">
            <Form form={form} onSubmit={apply}>
              <SelectFormField name="outcome" label="Outcome" options={OUTCOME_OPTIONS} required />
              <TextAreaFormField
                name="instructions"
                label="Instructions"
                rows={4}
                description="A resolution note or routing rationale."
              />
              <JsonFormField
                name="payload"
                label="Payload"
                rows={5}
                description="Optional JSON passed to the outcome."
              />
              <div className="flex flex-wrap gap-2">
                <SubmitButton>Apply outcome</SubmitButton>
                <Button type="button" variant="secondary" onClick={() => setRejecting(true)}>
                  <XCircle size={14} aria-hidden="true" />
                  Reject
                </Button>
              </div>
            </Form>
            {recommended.length > 0 && (
              <div className="mt-5 border-t pt-4">
                <p className="mb-2 text-sm font-medium text-heading">Recommended outcomes</p>
                <div className="flex flex-col gap-2">
                  {recommended.map((item, index) => {
                    const outcome = OUTCOMES.find((candidate) => candidate === item.outcome);
                    return (
                      <Button
                        key={`${String(item.outcome)}-${index}`}
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!outcome}
                        onClick={() => outcome && form.setValue('outcome', outcome, { shouldDirty: true })}
                      >
                        <CheckCircle size={14} className="text-success" aria-hidden="true" />
                        {String(item.label ?? item.outcome ?? 'Outcome')}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </Can>
        ) : (
          <p className="text-sm text-muted">This request is {request.status}, so it can no longer be routed.</p>
        )}
      </Card>
      <ActionRequestRejectModal request={rejecting ? request : null} onClose={() => setRejecting(false)} />
    </>
  );
};
